import { useNavigate, useLocation } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { LogOut, Flame, UserCog, ShieldCheck } from "lucide-react";
import { useAuth, useRoleHome } from "@/contexts/AuthContext";
import FeedbackReportButton from "./FeedbackReportButton";
import IntegrationHealthIndicator from "@/components/admin/IntegrationHealthIndicator";

const Header = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const role = (user?.role || "").toUpperCase();
  const isAdmin = role === "ADMIN";
  const homeRoute = useRoleHome();

  const handleLogout = () => {
    // Clear all auth-related storage
    localStorage.removeItem("token");
    localStorage.removeItem("userRole");
    sessionStorage.clear();
    
    // Call logout to clear React state
    logout();
    
    // Force a complete page reload with cache bypass
    // Adding timestamp to bust Safari's aggressive caching
    window.location.replace("/app/login?t=" + Date.now());
  };

  const handleLogoClick = () => {
    // iter193 — role-aware home. MOT_TESTER → /app/tech/mot;
    // TECH → /app/tech/jobs; FOH/ADMIN → /app/foh.
    navigate(homeRoute);
  };

  // Get current page for feedback context
  const currentPage = location.pathname;

  return (
    <header className="bg-white border-b px-4 py-3 flex justify-between items-center">
      {/* INTERNAL ONLY: Yestimate logo as home anchor */}
      <div 
        className="flex items-center gap-3 cursor-pointer" 
        onClick={handleLogoClick}
        data-testid="header-logo"
      >
        <img 
          src="/yestimate-logo.jpeg" 
          alt="Yestimate" 
          className="h-28 w-auto"
          style={{ maxHeight: '120px' }}
        />
      </div>
      <div className="flex items-center gap-4">
        {/* Feedback Report Button */}
        <FeedbackReportButton currentPage={currentPage} />
        
        {/* Friction Dashboard Link (FOH/Admin only) */}
        {!["tech", "technician"].includes(user?.role?.toLowerCase()) && (
          <Button 
            variant="ghost" 
            size="sm" 
            onClick={() => navigate("/app/admin/friction-dashboard")}
            className="text-slate-500 hover:text-orange-600"
            data-testid="friction-dashboard-btn"
          >
            <Flame className="w-4 h-4" />
          </Button>
        )}
        
        <Badge variant="outline" className="text-xs" data-testid="header-role-badge">
          {isAdmin
            ? "Admin"
            : currentPage.startsWith("/app/tech/mot")
            ? "MOT Tester"
            : currentPage.startsWith("/app/tech")
            ? "Technician"
            : currentPage.startsWith("/app/foh")
            ? "Service Advisor"
            : currentPage.startsWith("/app/admin")
            ? "Admin"
            : ["tech", "technician"].includes(user?.role?.toLowerCase())
            ? "Technician"
            : "Service Advisor"}
        </Badge>
        <button
          onClick={() => navigate("/app/profile")}
          className="text-sm text-gray-600 hover:text-slate-900 hover:underline underline-offset-4 transition-colors"
          data-testid="header-profile-link"
          title="Profile & password"
        >
          {user?.name}
        </button>
        {isAdmin && (
          <IntegrationHealthIndicator user={user} />
        )}
        {isAdmin && (
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate("/app/admin/staff")}
            className="text-slate-500 hover:text-slate-900"
            data-testid="header-admin-staff-btn"
            title="Staff & permissions"
          >
            <ShieldCheck className="w-4 h-4 mr-1" />
            Staff
          </Button>
        )}
        <Button variant="ghost" size="sm" onClick={handleLogout} data-testid="logout-btn">
          <LogOut className="w-4 h-4" />
        </Button>
      </div>
    </header>
  );
};

export default Header;
